
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// Define schema to exactly match Supabase's contact_messages table requirements
const contactSchema = z.object({
  name: z.string().min(2, 'Navn skal være mindst 2 karakterer langt'),
  email: z.string().email('Ugyldig email adresse'),
  message: z.string().min(10, 'Beskeden skal være mindst 10 karakterer lang'),
});

// This will now match what Supabase expects for insert operations
type ContactFormData = z.infer<typeof contactSchema>;

export function ContactForm() {
  const { toast } = useToast();
  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      message: '',
    }
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Now this will match the expected type for Supabase's insert operation
      const { error } = await supabase
        .from('contact_messages')
        .insert({
          name: data.name,
          email: data.email,
          message: data.message,
        });

      if (error) throw error;

      toast({
        title: "Beskeden blev sendt!",
        description: "Vi vender tilbage til dig hurtigst muligt.",
      });
      
      form.reset();
    } catch (error) {
      toast({
        title: "Der skete en fejl",
        description: "Prøv venligst igen senere.",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Navn</FormLabel>
              <FormControl>
                <Input placeholder="Dit navn" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="din@email.dk" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Besked</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Skriv din besked her..." 
                  className="min-h-[150px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          Send besked
        </Button>
      </form>
    </Form>
  );
}
